import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/data/api";
import { env } from "@/env";

// Google Workspace integration (Settings > Integrations). Connecting is a
// full-page OAuth redirect through the backend; status + disconnect are
// regular session-authenticated calls.

interface GoogleRoomCalendarRow {
  id: string;
  calendar_id: string;
  resource_name: string;
  device_id: string | null;
  last_synced_at: string | number | null;
  last_sync_error: string | null;
}

interface GoogleConnection {
  google_email: string;
  scopes: string[];
  connected_at: string | number;
  last_sync_at: string | number | null;
  last_sync_error: string | null;
  rooms: GoogleRoomCalendarRow[];
}

interface GoogleIntegrationStatus {
  configured: boolean;
  connection: GoogleConnection | null;
}

// One row per room in the workspace's Google Directory, merged with any
// device mapping. missing_from_directory marks a mapped room whose resource
// was deleted at Google — visible so the admin can clear the stale mapping.
export interface GoogleDirectoryRoom {
  calendar_id: string;
  resource_name: string;
  building_id: string | null;
  capacity: number | null;
  missing_from_directory: boolean;
  device_id: string | null;
  last_synced_at: string | number | null;
  last_sync_error: string | null;
}

const integrationKeys = {
  google: (workspaceSlug: string | null | undefined) =>
    ["integrations", "google", workspaceSlug ?? null] as const,
  googleRooms: (workspaceSlug: string | null | undefined) =>
    ["integrations", "google", "rooms", workspaceSlug ?? null] as const,
};

export function useGoogleIntegrationQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: integrationKeys.google(workspaceSlug),
    queryFn: () =>
      apiJson<GoogleIntegrationStatus>(
        `/workspaces/${encodeURIComponent(workspaceSlug ?? "")}/integrations/google`,
      ),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function useDisconnectGoogleMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ ok: true }>(
        `/workspaces/${encodeURIComponent(workspaceSlug ?? "")}/integrations/google`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: integrationKeys.google(workspaceSlug),
      });
    },
  });
}

export function useGoogleRoomsQuery(
  workspaceSlug: string | null | undefined,
  connected: boolean,
) {
  return useQuery({
    queryKey: integrationKeys.googleRooms(workspaceSlug),
    queryFn: async () => {
      const body = await apiJson<{ rooms: GoogleDirectoryRoom[] }>(
        `/workspaces/${encodeURIComponent(workspaceSlug ?? "")}/integrations/google/rooms`,
      );
      return body.rooms ?? [];
    },
    enabled: Boolean(workspaceSlug) && connected,
    // Sync state advances as the calendar scheduler ticks (~60s); keep the
    // table's "last sync" column loosely live.
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useMapRoomMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { calendarId: string; deviceId: string | null }) =>
      apiJson<{ room: GoogleDirectoryRoom | null }>(
        `/workspaces/${encodeURIComponent(workspaceSlug ?? "")}/integrations/google/rooms/${encodeURIComponent(input.calendarId)}`,
        { method: "PUT", json: { device_id: input.deviceId } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: integrationKeys.googleRooms(workspaceSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: integrationKeys.google(workspaceSlug),
      });
    },
  });
}

// Top-level navigation (not fetch) so the backend session cookie rides along
// and the 302 chain lands on Google's consent screen.
export function googleConnectUrl(workspaceSlug: string): string {
  return `${env.API_BASE_URL}/workspaces/${encodeURIComponent(workspaceSlug)}/integrations/google/connect`;
}
